import { useEffect, useRef, useCallback, useState } from "react";
import {
  establishPrivateChatPresenceConnection,
  removePrivateChatPresenceListener,
  PrivateChatPresencePayload,
} from "../services/privateChatPresenceSignalRService";
import { privateChatRealtimeEvent } from "../api/chatApi";
import { loadOtherSession } from "../utils/sessionManager";

type PresenceManagerParams = {
  myUserId?: string;
  requestToken?: string;
  identifier?: string;
};

type PresenceManagerReturn = {
  otherUserInChat: boolean;
  otherUserTyping: boolean;
  presenceChatId: string | null;
  sendRealtimeEvent: (
    type:
      | "chat_active"
      | "chat_inactive"
      | "chat_heartbeat"
      | "typing_start"
      | "typing_stop",
  ) => Promise<void>;
};

export function usePresenceManager({
  myUserId,
  requestToken,
  identifier,
}: PresenceManagerParams): PresenceManagerReturn {
  const [presenceChatId, setPresenceChatId] = useState<string | null>(null);
  const [presenceReady, setPresenceReady] = useState(false);
  const [otherUserInChat, setOtherUserInChat] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);

  const realtimeLoopRef = useRef<number | null>(null);
  const remoteTimeoutRef = useRef<number | null>(null);
  const lastRemoteUpdateRef = useRef<number>(0);
  const isInputFocusedRef = useRef(false);

  // Clear remote timeout helper
  const clearRemoteTimeout = useCallback(() => {
    if (remoteTimeoutRef.current) {
      window.clearTimeout(remoteTimeoutRef.current);
      remoteTimeoutRef.current = null;
    }
  }, []);

  // Refresh remote timeout helper
  const refreshRemoteTimeout = useCallback(() => {
    lastRemoteUpdateRef.current = Date.now();
    clearRemoteTimeout();

    remoteTimeoutRef.current = window.setTimeout(() => {
      const elapsed = Date.now() - lastRemoteUpdateRef.current;
      if (elapsed >= 20_000) {
        console.log(
          "⏱️ No remote presence update for 20s. Clearing web status.",
        );
        setOtherUserTyping(false);
        setOtherUserInChat(false);
      }
    }, 20_000);
  }, [clearRemoteTimeout]);

  // Send realtime event
  const sendRealtimeEvent = useCallback(
    async (
      type:
        | "chat_active"
        | "chat_inactive"
        | "chat_heartbeat"
        | "typing_start"
        | "typing_stop",
    ) => {
      if (!requestToken || !presenceChatId) {
        console.log("⏸️ Cannot send web realtime event:", {
          type,
          hasToken: Boolean(requestToken),
          presenceChatId,
        });
        return;
      }

      try {
        await privateChatRealtimeEvent(requestToken, type, presenceChatId);
      } catch (error) {
        console.warn("Failed to send web realtime event", type, error);
      }
    },
    [requestToken, presenceChatId],
  );

  // Poll for chatId until found
  useEffect(() => {
    if (!myUserId || !requestToken) return;

    let cancelled = false;
    let attempts = 0;
    let intervalId: number | null = null;

    const findChatId = () => {
      if (cancelled) return;

      const otherSession = loadOtherSession();
      const chatId = otherSession?.chatId;

      console.log("🔎 Checking presence chatId:", {
        attempt: attempts,
        chatId,
      });

      if (chatId) {
        setPresenceChatId((previous) => {
          if (previous !== chatId) {
            console.log("✅ Presence chatId found:", chatId);
          }
          return chatId;
        });

        setPresenceReady(true);

        if (intervalId !== null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }

      attempts += 1;
    };

    findChatId();
    intervalId = window.setInterval(findChatId, 250);

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [myUserId, requestToken, identifier]);

  // Establish SignalR presence connection
  useEffect(() => {
    if (!myUserId || !requestToken || !presenceReady || !presenceChatId) {
      console.log("⏸️ Web presence connect not ready:", {
        myUserId,
        hasToken: Boolean(requestToken),
        presenceReady,
        presenceChatId,
      });
      return;
    }

    let cancelled = false;

    console.log("🟦 Web presence connecting:", {
      myUserId,
      presenceChatId,
    });

    const handleRealtimeEvent = (payload: PrivateChatPresencePayload) => {
      if (cancelled) return;

      if (
        payload.type === "message_created" ||
        payload.type === "private_chat_seen" ||
        payload.type === "updatedChat" ||
        payload.type === "chatDeleted" ||
        payload.type === "chatTransferred"
      ) {
        return;
      }

      if (!payload.chatId || payload.chatId !== presenceChatId) {
        console.log("⚠️ Web ignored presence because chatId mismatch:", {
          payloadChatId: payload.chatId,
          presenceChatId,
        });
        return;
      }

      if (payload.type === "presence_changed") {
        if (payload.inChat) {
          setOtherUserInChat(true);
          refreshRemoteTimeout();
        } else {
          setOtherUserTyping(false);
          setOtherUserInChat(false);
          clearRemoteTimeout();
        }

        return;
      }

      if (payload.type === "typing_changed") {
        if (payload.typing) {
          setOtherUserTyping(true);
          setOtherUserInChat(true);
          refreshRemoteTimeout();
        } else {
          setOtherUserTyping(false);
          refreshRemoteTimeout();
        }

        return;
      }
    };

    establishPrivateChatPresenceConnection(
      myUserId,
      requestToken,
      handleRealtimeEvent,
    ).catch((error) => {
      console.error("❌ Failed to establish web presence SignalR:", error);
    });

    return () => {
      cancelled = true;
      removePrivateChatPresenceListener(handleRealtimeEvent);
    };
  }, [
    myUserId,
    requestToken,
    presenceReady,
    presenceChatId,
    refreshRemoteTimeout,
    clearRemoteTimeout,
  ]);

  // Realtime event loop - only keeps chat presence alive.
  // Typing is controlled only by real text changes in NewChatPageReady.
  useEffect(() => {
    if (!requestToken || !presenceChatId) {
      console.log("⏸️ Web realtime loop not started:", {
        hasToken: Boolean(requestToken),
        presenceChatId,
      });
      return;
    }

    let cancelled = false;

    const sendCurrentState = async (initial = false) => {
      if (cancelled || document.visibilityState !== "visible") return;

      const eventType = initial ? "chat_active" : "chat_heartbeat";

      try {
        await privateChatRealtimeEvent(requestToken, eventType, presenceChatId);
      } catch (error) {
        console.warn("Failed web realtime event:", eventType, error);
      }
    };

    sendCurrentState(true);

    if (realtimeLoopRef.current) {
      window.clearInterval(realtimeLoopRef.current);
    }

    realtimeLoopRef.current = window.setInterval(() => {
      sendCurrentState(false);
    }, 2_000);

    return () => {
      cancelled = true;

      if (realtimeLoopRef.current) {
        window.clearInterval(realtimeLoopRef.current);
        realtimeLoopRef.current = null;
      }
    };
  }, [requestToken, presenceChatId]);

  // Handle visibility changes and beforeunload
  useEffect(() => {
    if (!requestToken || !presenceChatId) return;

    const sendInactive = () => {
      if (!requestToken || !presenceChatId) return;

      console.log("📡 Web realtime inactive:", presenceChatId);

      privateChatRealtimeEvent(
        requestToken,
        "typing_stop",
        presenceChatId,
      ).catch(() => {});

      privateChatRealtimeEvent(
        requestToken,
        "chat_inactive",
        presenceChatId,
      ).catch(() => {});
    };

    const sendVisible = () => {
      if (!requestToken || !presenceChatId) return;

      console.log("📡 Web realtime visible: chat_active", presenceChatId);

      // Important:
      // Returning to the page should not show "typing".
      // Only a real text change should do that.
      privateChatRealtimeEvent(
        requestToken,
        "chat_active",
        presenceChatId,
      ).catch(() => {});
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sendInactive();
      } else {
        sendVisible();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", sendInactive);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", sendInactive);

      sendInactive();
      clearRemoteTimeout();
      setOtherUserTyping(false);
      setOtherUserInChat(false);
    };
  }, [requestToken, presenceChatId, clearRemoteTimeout]);

  return {
    otherUserInChat,
    otherUserTyping,
    presenceChatId,
    sendRealtimeEvent,
  };
}
